import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../context/AuthContext"
import { useConfig } from "../../context/ConfigContext"
import API from "../../api/axios"
import "./enroll.css"

/**
 * EnrollModal — handles both:
 *  1. Free / Trial courses  → direct enrollment, no payment
 *  2. Paid courses          → payment details form → pending → admin approves
 *
 * Props:
 *   course   : course object
 *   isOpen   : boolean
 *   onClose  : fn
 */
const EnrollModal = ({ course, isOpen, onClose }) => {
  const navigate             = useNavigate()
  const { user }             = useAuth()
  const { paymentEnabled }   = useConfig()
  const isFree               = course?.isFree || course?.isTrial
  const showComingSoon        = !isFree && !paymentEnabled  // paid + payments disabled

  const [step, setStep]       = useState(1)
  const [method, setMethod]   = useState("mpesa")
  const [phone, setPhone]     = useState(user?.phone || "")
  const [loading, setLoading] = useState(false)
  const [errMsg, setErrMsg]   = useState("")
  const [paymentData, setPaymentData] = useState(null)

  if (!isOpen || !course) return null

  const reset = () => {
    setStep(1); setMethod("mpesa"); setPhone("")
    setLoading(false); setErrMsg(""); setPaymentData(null)
  }
  const handleClose = () => { reset(); onClose() }

  /* ── Step 1: Confirm → free enroll ── */
  const handleFreeEnroll = async () => {
    setLoading(true); setErrMsg("")
    try {
      await API.post("/enrollments", { courseId: course.id })
      setStep(3)
    } catch (err) {
      const msg = err.response?.data?.message || "Enrollment failed."
      if (msg.includes("Already enrolled")) {
        setStep(3) // treat as success
      } else {
        setErrMsg(msg); setStep(4)
      }
    } finally {
      setLoading(false)
    }
  }

  /* ── Step 2: Submit payment ── */
  const handlePayment = async (e) => {
    e.preventDefault()
    if (method === "mpesa" && !phone.trim())
      return setErrMsg("Please enter your M-Pesa phone number.")

    setLoading(true); setErrMsg("")
    try {
      const { data } = await API.post("/payments/initiate", {
        courseId: course.id,
        method,
        phone: phone.trim(),
      })
      setPaymentData(data)
      setStep(3)
    } catch (err) {
      const msg = err.response?.data?.message || "Payment initiation failed."
      if (msg.includes("Already enrolled")) {
        setStep(3)
      } else {
        setErrMsg(msg); setStep(4)
      }
    } finally {
      setLoading(false)
    }
  }

  const goToDashboard = () => { handleClose(); navigate("/dashboard") }

  return (
    <>
      <div className="enroll-backdrop" onClick={handleClose} />
      <div className="enroll-modal" role="dialog" aria-modal="true">

        {/* Header */}
        <div className="enroll-modal-header">
          <div className="enroll-course-info">
            <div className="enroll-course-icon"><i className="fa fa-chart-line"></i></div>
            <div>
              <h3>{course.title}</h3>
              <span>{course.duration} · {course.level}</span>
            </div>
          </div>
          <button className="enroll-close-btn" onClick={handleClose}>
            <i className="fa fa-times"></i>
          </button>
        </div>

        {/* ── Step 1: Confirm ── */}
        {step === 1 && (
          <div className="enroll-body">
            <div className="enroll-step-label">
              <i className="fa fa-info-circle"></i> Confirm Enrollment
            </div>

            <div className="enroll-summary">
              <div className="enroll-summary-row">
                <span>Course</span>
                <strong>{course.title}</strong>
              </div>
              <div className="enroll-summary-row">
                <span>Duration</span>
                <strong>{course.duration || "—"}</strong>
              </div>
              <div className="enroll-summary-row">
                <span>Level</span>
                <strong style={{ textTransform: "capitalize" }}>{course.level}</strong>
              </div>
              <div className="enroll-summary-row">
                <span>Instructor</span>
                <strong>{course.instructor || "Mr. Ali"}</strong>
              </div>
              <div className="enroll-summary-row">
                <span>Student</span>
                <strong>{user?.name || "—"}</strong>
              </div>
              <div className="enroll-summary-row">
                <span>Email</span>
                <strong>{user?.email || "—"}</strong>
              </div>
              <div className="enroll-summary-row total">
                <span>Price</span>
                <strong>
                  {isFree
                    ? <span className="price-free-tag">FREE</span>
                    : `KES ${Number(course.price).toLocaleString()}`
                  }
                </strong>
              </div>
            </div>

            {isFree ? (
              <button
                className="enroll-btn primary"
                onClick={handleFreeEnroll}
                disabled={loading}
              >
                {loading
                  ? <><i className="fa fa-spinner fa-spin"></i> Enrolling…</>
                  : <><i className="fa fa-play-circle"></i> Start Free Now</>
                }
              </button>
            ) : showComingSoon ? (
              /* ── Payment disabled ── */
              <div className="enroll-coming-soon">
                <div className="coming-soon-icon"><i className="fa fa-clock"></i></div>
                <h4>Payments Coming Soon</h4>
                <p>Online payments are not yet active. To enroll, please contact us directly.</p>
                <div className="coming-soon-actions">
                  <a
                    href="https://wa.me/254722525826"
                    target="_blank"
                    rel="noreferrer"
                    className="enroll-btn primary"
                    style={{ textDecoration: "none", justifyContent: "center" }}
                  >
                    <i className="fab fa-whatsapp"></i> WhatsApp Us
                  </a>
                  <a
                    href="mailto:info@rovers.ke"
                    className="enroll-btn ghost"
                    style={{ textDecoration: "none", justifyContent: "center" }}
                  >
                    <i className="fa fa-envelope"></i> Email Us
                  </a>
                </div>
              </div>
            ) : (
              <button className="enroll-btn primary" onClick={() => setStep(2)}>
                <i className="fa fa-lock-open"></i> Proceed to Payment
              </button>
            )}
            <button className="enroll-btn ghost" onClick={handleClose}>Cancel</button>
          </div>
        )}

        {/* ── Step 2: Payment details ── */}
        {step === 2 && (
          <div className="enroll-body">
            <div className="enroll-step-label">
              <i className="fa fa-credit-card"></i> Payment Details
            </div>

            <div className="enroll-amount-banner">
              <span>Total Due</span>
              <strong>KES {Number(course.price).toLocaleString()}</strong>
            </div>

            {errMsg && (
              <div className="enroll-error">
                <i className="fa fa-exclamation-circle"></i> {errMsg}
              </div>
            )}

            <form onSubmit={handlePayment}>
              {/* Payment method */}
              <div className="enroll-field">
                <label>Payment Method</label>
                <div className="enroll-methods">
                  {[
                    { id: "mpesa", icon: "fa fa-mobile-alt",   label: "M-Pesa" },
                    { id: "bank",  icon: "fa fa-university",   label: "Bank Transfer" },
                    { id: "manual",icon: "fa fa-hand-holding-usd", label: "Manual / Cash" },
                  ].map(m => (
                    <label key={m.id} className={`enroll-method ${method === m.id ? "selected" : ""}`}>
                      <input
                        type="radio"
                        name="method"
                        value={m.id}
                        checked={method === m.id}
                        onChange={() => setMethod(m.id)}
                      />
                      <i className={m.icon}></i>
                      {m.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* M-Pesa phone */}
              {method === "mpesa" && (
                <div className="enroll-field">
                  <label>M-Pesa Phone Number *</label>
                  <input
                    type="tel"
                    placeholder="e.g. 0722 000 200"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    required
                  />
                  <small>Enter the number you'll use to complete the M-Pesa payment</small>
                </div>
              )}

              {/* Bank / Manual instructions */}
              {(method === "bank" || method === "manual") && (
                <div className="enroll-info-box">
                  <i className="fa fa-info-circle"></i>
                  <div>
                    <strong>Payment Instructions</strong>
                    <p>
                      After submitting, our team will contact you with bank details or cash
                      payment instructions. Your enrollment will be activated once payment
                      is confirmed by admin.
                    </p>
                  </div>
                </div>
              )}

              {/* User details recap */}
              <div className="enroll-user-recap">
                <div className="enroll-recap-row">
                  <i className="fa fa-user"></i>
                  <span>{user?.name}</span>
                </div>
                <div className="enroll-recap-row">
                  <i className="fa fa-envelope"></i>
                  <span>{user?.email}</span>
                </div>
                {user?.phone && (
                  <div className="enroll-recap-row">
                    <i className="fa fa-phone"></i>
                    <span>{user.phone}</span>
                  </div>
                )}
              </div>

              <div className="enroll-actions">
                <button type="button" className="enroll-btn ghost" onClick={() => setStep(1)}>
                  <i className="fa fa-arrow-left"></i> Back
                </button>
                <button type="submit" className="enroll-btn primary" disabled={loading}>
                  {loading
                    ? <><i className="fa fa-spinner fa-spin"></i> Submitting…</>
                    : <><i className="fa fa-paper-plane"></i> Submit & Confirm</>
                  }
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ── Step 3: Success ── */}
        {step === 3 && (
          <div className="enroll-body enroll-success">
            <div className="enroll-success-icon">
              <i className={isFree ? "fa fa-play-circle" : "fa fa-check-circle"}></i>
            </div>
            <h3>{isFree ? "You're Enrolled!" : "Request Submitted!"}</h3>

            {isFree ? (
              <p>
                You now have full access to <strong>{course.title}</strong>.
                Head to your dashboard to start learning.
              </p>
            ) : (
              <>
                <p>
                  Your payment request for <strong>{course.title}</strong> has been received.
                </p>
                {paymentData && (
                  <div className="enroll-payment-ref">
                    <div className="enroll-ref-row">
                      <span>Amount</span>
                      <strong>KES {Number(course.price).toLocaleString()}</strong>
                    </div>
                    <div className="enroll-ref-row">
                      <span>Method</span>
                      <strong style={{ textTransform: "uppercase" }}>{method}</strong>
                    </div>
                    {paymentData.instructions && (
                      <div className="enroll-instructions">
                        <i className="fa fa-info-circle"></i>
                        {paymentData.instructions}
                      </div>
                    )}
                  </div>
                )}
                <p className="enroll-pending-note">
                  <i className="fa fa-clock"></i>
                  Your enrollment will be activated once admin confirms your payment.
                  You'll receive an email notification.
                </p>
              </>
            )}

            <button className="enroll-btn primary" onClick={goToDashboard}>
              <i className="fa fa-th-large"></i> Go to My Dashboard
            </button>
            <button className="enroll-btn ghost" onClick={handleClose}>
              Back to Courses
            </button>
          </div>
        )}

        {/* ── Step 4: Error ── */}
        {step === 4 && (
          <div className="enroll-body enroll-error-state">
            <div className="enroll-error-icon">
              <i className="fa fa-exclamation-triangle"></i>
            </div>
            <h3>Something went wrong</h3>
            <p>{errMsg}</p>
            <button className="enroll-btn primary" onClick={() => { setStep(isFree ? 1 : 2); setErrMsg("") }}>
              Try Again
            </button>
            <button className="enroll-btn ghost" onClick={handleClose}>Cancel</button>
          </div>
        )}

      </div>
    </>
  )
}

export default EnrollModal
